import type { HTMLAttributes } from 'react';
import { cx } from '@parrot/utils';
import styles from './ScrollShadow.module.css';

type Props = HTMLAttributes<HTMLDivElement> & {
  /**
   * Which way the content scrolls. One axis only: the shadows are the element's two
   * pseudo-elements, and there is no third and fourth to pin to the other edges.
   */
  axis?: 'x' | 'y';
};

/**
 * A scroll container that fades a shadow in at whichever edge has more content past it.
 *
 * The shadow is a sticky pseudo-element, so it needs to know nothing about what's behind
 * it — it darkens whatever is there. The earlier attempt used opaque gradients that had
 * to match the surrounding colour, and painted visible bands whenever they didn't.
 *
 * Opacity is driven by a scroll-driven CSS animation, so there's no listener to fall out
 * of date when content changes size. Browsers without it get no shadow and scroll as
 * normal.
 *
 * Pad the content, not this. Sticky positions against the content box, so padding here
 * insets the shadows by that much and leaves a gap at the edge.
 */
export const ScrollShadow = ({ axis = 'y', className, ...divProps }: Props) => (
  <div className={cx(styles.base, styles[axis], className)} {...divProps} />
);
